import React, { Fragment, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { Link } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import CopyrightIcon from '@mui/icons-material/Copyright';
import '../shared.css';

const InterCityStockInPending = () => {
    const { id } = useParams();
    const { isAuthenticated, companyId, branchId, token, logout } = useAuth();

    const [formData, setFormData]         = useState({});
    const [invoiceDetails, setInvoiceDetails] = useState([]);
    const [loadError, setLoadError]       = useState('');

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
    }, [isAuthenticated, companyId, branchId, token, logout]);

    useEffect(() => {
        if (!id) return;
        fetchData('InterCityStockTransferApi', `/${id}`, token)
            .then(r => {
                const rd = r?.response_data;
                if (!rd) { setLoadError('No data returned from server.'); return; }
                if (rd.interCityStockTransfer) {
                    setFormData(rd.interCityStockTransfer);
                    setInvoiceDetails(rd.InterCityStockTransferDetails || []);
                } else {
                    setFormData(rd);
                    setInvoiceDetails([]);
                }
            })
            .catch(err => {
                console.error('Error loading InterCity StockIn pending:', err);
                setLoadError('Failed to load data. Please try again.');
            });
    }, [id]);

    const fmt = (d) => {
        if (!d) return '';
        const dt = new Date(d);
        return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
    };
    const sf = (v) => v != null ? parseFloat(v).toFixed(2) : '0.00';

    const PageHeader = () => (
        <div className="pg-header">
            <h5 className="pg-title">Inter City Stock Transfer – Approval Pending</h5>
            <div className="pg-header-actions">
                <Link to="/Home" className="pg-btn pg-btn-danger">
                    <CloseIcon /> Back to Home
                </Link>
            </div>
        </div>
    );

    if (!formData.Id && !loadError) {
        return (
            <div className="pg-container">
                <PageHeader />
                <div style={{ padding: 24, color: '#94a3b8', fontSize: 13 }}>Loading…</div>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="pg-container">
                <PageHeader />
                <div className="pg-alert pg-alert-error" style={{ margin: 16 }}>{loadError}</div>
            </div>
        );
    }

    return (
        <Fragment>
            <div className="pg-container">
                <PageHeader />

                <div className="pg-card">
                    {/* Header fields */}
                    <div className="pg-hpanel">
                        <div className="pg-hcell" style={{ flex: '1 1 0' }}>
                            <span className="pg-hcell-label">Bill Date</span>
                            <input className="pg-hcell-input pg-hcell-readonly" type="date"
                                value={fmt(formData.BillDate)} disabled />
                        </div>
                        <div className="pg-hcell" style={{ flex: '1 1 0' }}>
                            <span className="pg-hcell-label">Bill Number</span>
                            <input className="pg-hcell-input pg-hcell-readonly" type="text"
                                value={`${formData.BillPrefix || ''}/${formData.BillNumber || ''}/${formData.FinYear || ''}`}
                                readOnly />
                        </div>
                        <div className="pg-hcell" style={{ flex: '2 1 0' }}>
                            <span className="pg-hcell-label">To Branch</span>
                            <input className="pg-hcell-input pg-hcell-readonly" type="text"
                                value={formData.ToBranchName || formData.CustomerName || ''} readOnly />
                        </div>
                        <div className="pg-hcell" style={{ flex: '0 0 80px' }}>
                            <span className="pg-hcell-label">Total Qty</span>
                            <input className="pg-hcell-input pg-hcell-readonly text-right" type="text"
                                value={formData.TotalQuantity || 0} readOnly />
                        </div>
                    </div>

                    {/* Items table */}
                    <div className="pg-invoice-table-wrap" style={{ marginTop: 12 }}>
                        <table className="pg-invoice-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Item Name</th>
                                    <th className="right">Qty</th>
                                    <th className="right">Rate</th>
                                    <th className="right">Disc %</th>
                                    <th className="right">GST %</th>
                                    <th className="right">Taxable</th>
                                    <th className="right">CGST</th>
                                    <th className="right">SGST</th>
                                    <th className="right">IGST</th>
                                    <th className="right">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoiceDetails.length === 0
                                    ? <tr><td colSpan={11} style={{ textAlign: 'center', color: '#94a3b8', padding: 14 }}>No items found.</td></tr>
                                    : invoiceDetails.map((item, i) => (
                                        <tr key={i}>
                                            <td style={{ textAlign: 'center', width: 36 }}>{i + 1}</td>
                                            <td>{item.ItemName}</td>
                                            <td className="right">{item.Quantity}</td>
                                            <td className="right">{sf(item.Rate)}</td>
                                            <td className="right">{item.DiscountPercentage}</td>
                                            <td className="right">{item.GSTRate}</td>
                                            <td className="right">{sf(item.TaxableAmount)}</td>
                                            <td className="right">{sf(item.CGSTAmount)}</td>
                                            <td className="right">{sf(item.SGSTAmount)}</td>
                                            <td className="right">{sf(item.IGSTAmount)}</td>
                                            <td className="right" style={{ fontWeight: 700 }}>{sf(item.TotalAmount)}</td>
                                        </tr>
                                    ))
                                }
                            </tbody>
                        </table>
                    </div>

                    {/* Totals row */}
                    <div className="pg-hpanel" style={{ marginTop: 8 }}>
                        <div className="pg-hcell" style={{ flex: '3 1 0' }}>
                            <span className="pg-hcell-label">Remarks</span>
                            <input className="pg-hcell-input pg-hcell-readonly" type="text"
                                value={formData.Remarks || ''} readOnly />
                        </div>
                        {[
                            ['Taxable Amt', 'TaxableAmount'],
                            ['CGST',        'CGSTAmount'],
                            ['SGST',        'SGSTAmount'],
                            ['IGST',        'IGSTAmount'],
                            ['Round Off',   'RoundOff'],
                            ['Grand Total', 'GrandTotal'],
                        ].map(([label, key]) => (
                            <div key={key} className="pg-hcell" style={{ flex: '1 1 0' }}>
                                <span className="pg-hcell-label">{label}</span>
                                <input className="pg-hcell-input pg-hcell-readonly text-right" type="text"
                                    value={sf(formData[key])} readOnly />
                            </div>
                        ))}
                    </div>
                </div>

                <div className="pg-watermark">
                    <CopyrightIcon style={{ fontSize: 12 }} /><b>EFFTABS SOLUTIONS LLP</b>
                </div>
            </div>
        </Fragment>
    );
};

export default InterCityStockInPending;
